import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useGoogleAnalytics, trackEvent } from '../../hooks/useGoogleAnalytics';
import { seoService } from '../../services/seoService';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import SchemaTypeSelector from './components/SchemaTypeSelector';
import LocalBusinessForm from './components/LocalBusinessForm';
import ServiceSchemaForm from './components/ServiceSchemaForm';
import ReviewSchemaForm from './components/ReviewSchemaForm';
import SchemaPreview from './components/SchemaPreview';
import { Code2, Sparkles, CheckCircle2, AlertCircle } from 'lucide-react';

const SchemaMarkupGenerator = () => {
  useGoogleAnalytics();

  const [selectedSchemaType, setSelectedSchemaType] = useState('LocalBusiness');
  const [formData, setFormData] = useState({});
  const [generatedSchema, setGeneratedSchema] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    trackEvent('page_view', { page_name: 'Schema Markup Generator' });
  }, []);

  const handleSchemaTypeChange = (schemaType) => {
    setSelectedSchemaType(schemaType);
    setFormData({});
    setGeneratedSchema(null);
    setValidationStatus(null);
    trackEvent('schema_type_selected', { schema_type: schemaType });
  };

  const handleFormUpdate = (data) => {
    setFormData(data);
  };

  const generateSchema = () => {
    let schema;
    
    switch (selectedSchemaType) {
      case 'LocalBusiness':
        schema = seoService?.generateLocalBusinessSchema(formData);
        break;
      case 'Service':
        schema = seoService?.generateServiceSchema(formData);
        break;
      case 'Review':
        schema = seoService?.generateReviewSchema(formData);
        break;
      default:
        schema = null;
    }

    setGeneratedSchema(schema);
    setValidationStatus('valid');
    trackEvent('schema_generated', { schema_type: selectedSchemaType });
  };

  const handleSaveSchema = async () => {
    if (!generatedSchema) return;

    setIsSaving(true);
    setSaveStatus(null);

    try {
      const schemaData = {
        page_url: formData?.pageUrl || window.location?.origin,
        schema_type: selectedSchemaType,
        schema_data: generatedSchema,
        is_active: true,
        validation_status: 'valid'
      };

      const result = await seoService?.saveSchemaMarkup(schemaData);

      if (result?.error) {
        throw result?.error;
      }

      setSaveStatus({ type: 'success', message: 'Schema markup saved successfully!' });
      trackEvent('schema_saved', { schema_type: selectedSchemaType });
    } catch (error) {
      console.error('Error saving schema:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save schema markup' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const renderForm = () => {
    switch (selectedSchemaType) {
      case 'LocalBusiness':
        return <LocalBusinessForm data={formData} onChange={handleFormUpdate} />;
      case 'Service':
        return <ServiceSchemaForm data={formData} onChange={handleFormUpdate} />;
      case 'Review':
        return <ReviewSchemaForm data={formData} onChange={handleFormUpdate} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Schema Markup Generator - Sailors Bail Bonds SEO Tools</title>
        <meta 
          name="description" 
          content="Generate and implement structured data schema markup for Sacramento County bail bonds with automated LocalBusiness, Service, and Review schemas." 
        />
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Code2 className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              Schema Markup Generator & Implementation
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Create and deploy structured data for enhanced search engine visibility
          </p>
        </div>

        {/* Save Status Alert */}
        {saveStatus && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            saveStatus?.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {saveStatus?.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <p className={`font-medium ${saveStatus?.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {saveStatus?.message}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Schema Type Selector */}
            <SchemaTypeSelector 
              selectedType={selectedSchemaType}
              onChange={handleSchemaTypeChange}
            />

            {/* Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  Configure {selectedSchemaType} Schema
                </h2>
              </div>
              
              {renderForm()}

              <div className="mt-6 flex gap-4">
                <button
                  onClick={generateSchema}
                  className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Generate Schema
                </button>
                
                {generatedSchema && (
                  <button
                    onClick={handleSaveSchema}
                    disabled={isSaving}
                    className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save & Deploy'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="lg:col-span-1">
            <SchemaPreview 
              schema={generatedSchema}
              schemaType={selectedSchemaType}
              validationStatus={validationStatus}
            />
          </div>
        </div>

        {/* Emergency Contact Banner */}
        <div className="mt-8 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-xl font-bold mb-1">Need Immediate Bail Assistance?</h3>
              <p className="text-purple-100">Sacramento County experts available 24/7</p>
            </div>
            <a
              href="tel:916-555-BAIL"
              onClick={() => trackEvent('emergency_call', { source: 'Schema Generator' })}
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-bold hover:bg-purple-50 transition-colors"
            >
              Call Now: 916-555-BAIL
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SchemaMarkupGenerator;